import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle } from 'lucide-react';
import { useContentReports } from '@/hooks/useContentReports';

type ContentType = 'review' | 'review_comment' | 'activity_comment' | 'message' | 'tag' | 'author_bio';
type ReportCategory = 'hate_speech' | 'harassment' | 'spam' | 'sexual_content' | 'violence' | 'misinformation' | 'other';

interface ReportContentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  contentType: ContentType;
  contentId: string;
  contentPreview?: string;
}

const REPORT_CATEGORIES: { value: ReportCategory; label: string; description: string }[] = [
  {
    value: 'hate_speech',
    label: 'Hate Speech',
    description: 'Content that promotes hate or discrimination',
  },
  {
    value: 'harassment',
    label: 'Harassment or Bullying',
    description: 'Content that harasses, intimidates, or bullies',
  },
  {
    value: 'spam',
    label: 'Spam or Advertising',
    description: 'Unwanted commercial content or spam',
  },
  {
    value: 'sexual_content',
    label: 'Inappropriate Sexual Content',
    description: 'Explicit or inappropriate sexual material',
  },
  {
    value: 'violence',
    label: 'Violence or Threats',
    description: 'Content promoting violence or containing threats',
  },
  {
    value: 'misinformation',
    label: 'False or Misleading Information',
    description: 'Content that spreads false information',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Other violations not listed above',
  },
];

export function ReportContentDialog({
  isOpen,
  onClose,
  contentType,
  contentId,
  contentPreview,
}: ReportContentDialogProps) {
  const [selectedCategory, setSelectedCategory] = useState<ReportCategory | null>(null);
  const [additionalDetails, setAdditionalDetails] = useState('');
  const { submitReport, isSubmitting } = useContentReports();

  const handleSubmit = async () => {
    if (!selectedCategory) return;

    // Require additional details for 'other' category
    if (selectedCategory === 'other' && !additionalDetails.trim()) {
      return;
    }

    const success = await submitReport({
      contentType,
      contentId,
      reportCategory: selectedCategory,
      additionalDetails: additionalDetails.trim() || undefined,
    });

    if (success) {
      // Reset form and close
      setSelectedCategory(null);
      setAdditionalDetails('');
      onClose();
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedCategory(null);
      setAdditionalDetails('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            Report Content
          </DialogTitle>
          <DialogDescription>
            Help us understand what's wrong with this content. Reports are reviewed by our moderation team.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 px-3 py-2.5 bg-muted/50 rounded-md border border-border/50 mt-4">
          <span className="text-lg flex-shrink-0">🦖</span>
          <p className="text-sm text-muted-foreground">
            Reporting helps keep our book club safe. Thanks for caring.
          </p>
        </div>

        <div className="space-y-4 py-4">
          {contentPreview && (
            <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground italic border">
              "{contentPreview.substring(0, 150)}{contentPreview.length > 150 ? '...' : ''}"
            </div>
          )}

          <div className="space-y-3">
            <Label>What's the issue?</Label>
            <RadioGroup value={selectedCategory || ''} onValueChange={(value) => setSelectedCategory(value as ReportCategory)}>
              {REPORT_CATEGORIES.map((category) => (
                <div key={category.value} className="flex items-start space-x-3 space-y-0">
                  <RadioGroupItem value={category.value} id={category.value} />
                  <div className="space-y-1 leading-none">
                    <Label
                      htmlFor={category.value}
                      className="text-sm font-medium cursor-pointer"
                    >
                      {category.label}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {category.description}
                    </p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="details">
              Additional details {selectedCategory === 'other' && <span className="text-destructive">*</span>}
            </Label>
            <Textarea
              id="details"
              placeholder="Provide any additional context that would help us review this report..."
              value={additionalDetails}
              onChange={(e) => setAdditionalDetails(e.target.value.slice(0, 500))}
              rows={3}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground text-right">
              {additionalDetails.length}/500 characters
            </p>
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedCategory || (selectedCategory === 'other' && !additionalDetails.trim()) || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
